// The onboarding takeover's resume record — which step was open, and whether it was mandatory.
//
// The connect step leaves the app for an OAuth consent screen and comes back with a full page load;
// v3 survived that by persisting `layoutStore.onboardingStep`. Only the POSITION survives here, in
// `sessionStorage` (one tab, gone with it), never the brand-context defaults, so a reload cannot
// resume against a stale seed (contract 29 §7) — step 2 re-reads the workspace's own attributes.

import type { OnboardingStepKey } from '../models/onboarding.types'

import { isOnboardingStepKey, normalizeOnboardingStep } from './onboardingSteps'

export type OnboardingMode = 'required' | 'optional'

export interface OnboardingSessionRecord {
  step: OnboardingStepKey
  mode: OnboardingMode
}

const STORAGE_KEY = 'orphex.onboarding.session'

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function readOnboardingSession(): OnboardingSessionRecord | null {
  try {
    const raw = storage()?.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<OnboardingSessionRecord>
    if (!isOnboardingStepKey(parsed.step)) return null
    const mode: OnboardingMode = parsed.mode === 'required' ? 'required' : 'optional'
    return { step: normalizeOnboardingStep(parsed.step), mode }
  } catch {
    return null
  }
}

export function writeOnboardingSession(record: OnboardingSessionRecord): void {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(record))
  } catch {
    // Quota / privacy mode: the takeover still works, it just restarts after an OAuth return.
  }
}

export function clearOnboardingSession(): void {
  try {
    storage()?.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clear.
  }
}
